// React and MobX
import React, { useState } from "react";
import { useObserver } from "mobx-react-lite";

// Material-UI Core and Styles
import { Box, FormControlLabel, IconButton, Radio, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import CheckIcon from "@material-ui/icons/Check";
import ClearIcon from "@material-ui/icons/Clear";
import { Close as CloseIcon } from "@material-ui/icons";

// Core Utilities and Routing
import { Loader } from "Core/Components/Loader";

// Custom Styles
import { ChoicesProductGroupStyles } from "Custom/StylesAppSpecific/DashboardSubViews/ChoicesProductGroupStyling";
import { DashboardPageStyles, usePageAnimationStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";
import { ChoicesAreaPageStyles } from "Custom/StylesAppSpecific/DashboardSubViews/ChoiceAreaPageStyling";

// Custom ViewModels
import { ChoicesAreaViewModel } from "Custom/ViewModels/DashboardSubViewModels/ChoicesAreaViewModel";
import { ProductGroupViewModel } from "Custom/ViewModels/DashboardSubViewModels/ProductGroupViewModel";
import { Stores, StoresContext } from "Custom/Stores";

// Custom Components
import { ChoicesProduct } from "Custom/Views/DashboardSubViews/ChoicesProduct";
import { NavButton } from "Custom/Components/Buttons/NavButton";
import { Error } from "Custom/Components/Error";
import { StandardButton } from "Custom/Components/Buttons/StandardButton";

// Enums and Models
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";

// Custom SVG Components
import { ReactComponent as SquareLeftArrow } from "../../Content/play-backward-icon.svg";

interface ChoicesProductGroupProps {
    propertyId: string;
    areaId: string;
    productGroupId: string;
    onClose?: () => void;
    stores: Stores;
}

export const ChoicesProductGroup: React.FC<ChoicesProductGroupProps> = ({ propertyId, areaId, productGroupId, onClose, stores }) => {
    const pageAnimationStyles = usePageAnimationStyles();
    const dashboardPageStyles = DashboardPageStyles();
    const choicesAreaPageStyles = ChoicesAreaPageStyles();
    const choicesProductGroupStyles = ChoicesProductGroupStyles();
    const portalThemeStore = stores.domain.PortalThemeStore;

    const [viewModel] = useState(() => new ChoicesAreaViewModel(propertyId, areaId));

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md")) ? true : false;

    const renderBusy = () => <Loader loadingText={viewModel.apiStatusLoadingMessage} />;
    const renderError = () => <Error errorText={viewModel.apiStatusErrorMessage} />;

    const renderAreaTitleRow = () => {
        const productGroup = viewModel.productGroups.find(pg => pg.model.id == productGroupId);

        return (
            productGroup && (
                <Box
                    className={`${choicesAreaPageStyles.titleRowHeader} mobileDrawerHeader`}
                    style={{
                        ...(portalThemeStore.primaryColour ? { backgroundColor: portalThemeStore.primaryColour } : {}),
                        ...(portalThemeStore.primaryColour ? { borderBottomColor: portalThemeStore.secondaryColour } : {}),
                    }}
                >
                    {!isDesktop && onClose && (
                        <IconButton onClick={onClose} className="closeBtn">
                            <CloseIcon />
                        </IconButton>
                    )}

                    <Box className={choicesAreaPageStyles.titleTextRow}>
                        <Typography variant={"h5"}>{productGroup.model.productGroupName}</Typography>
                    </Box>

                    <Box className={choicesAreaPageStyles.productGroupTitleContainer}>
                        <Box className={choicesAreaPageStyles.productGroupCounterContainer}>
                            <Box className={choicesAreaPageStyles.productGroupCounterContainerListRow}>
                                <Typography component="p">
                                    {productGroup.isChoiceSelected ? <CheckIcon style={{ color: "#fff" }} /> : <ClearIcon style={{ color: "#fff" }} />}
                                </Typography>
                                <Typography component="p" style={{ color: "#fff" }}>
                                    option selected
                                </Typography>
                            </Box>
                            <Box className={choicesAreaPageStyles.productGroupCounterContainerListRow}>
                                <Typography component="p" style={{ color: "#fff" }}>
                                    {productGroup.numberOfExtras}
                                </Typography>
                                <Typography component="p" style={{ color: "#fff" }}>
                                    extras added
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )
        );
    };

    const renderBackToChoicesOrArea = () => {
        const productGroup = viewModel.productGroups.find(pg => pg.model.id == productGroupId);

        return (
            <Box
                className={choicesAreaPageStyles.backTochoices}
                style={{
                    ...(portalThemeStore.secondaryColour ? { backgroundColor: portalThemeStore.secondaryColour } : {}),
                }}
            >
                <SquareLeftArrow
                    className="arrowIcon"
                    onClick={() => viewModel.navigateToChoices(propertyId)}
                    style={{ cursor: "pointer", ...(portalThemeStore.primaryColour ? { color: portalThemeStore.primaryColour } : {}) }}
                />
                <NavButton displayName="Back to choices" />

                <Box
                    className={choicesAreaPageStyles.backTochoicesArea}
                    style={{
                        ...(portalThemeStore.secondaryColour ? { backgroundColor: portalThemeStore.secondaryColour } : {}),
                    }}
                >
                    <SquareLeftArrow
                        className="arrowIcon"
                        onClick={() => viewModel.navigateToChoicesArea(propertyId, viewModel.model.id)}
                        style={{ cursor: "pointer", ...(portalThemeStore.primaryColour ? { color: portalThemeStore.primaryColour } : {}) }}
                    />
                    <NavButton displayName={productGroup ? `Back to ${viewModel.model.areaName.toLowerCase()}` : "Back to choices area"} />
                </Box>
            </Box>
        );
    };

    const renderChoices = () => {
        const productGroup = viewModel.productGroups.find(pg => pg.model.id == productGroupId);
        const isDisabled = viewModel.isChoicesLocked;

        return (
            productGroup &&
            productGroup.sortedProducts.map((product, index) => (
                <ChoicesProduct
                    key={`${product.model.productId}-${productGroup.model.id}`}
                    viewModel={product}
                    productGroupViewModel={productGroup}
                    index={index}
                    isExtra={false}
                    isDisabled={isDisabled}
                />
            ))
        );
    };

    const renderExtras = () => {
        const productGroup = viewModel.productGroups.find(pg => pg.model.id == productGroupId);
        const isDisabled = viewModel.isChoicesLocked;

        return (
            productGroup &&
            productGroup.hasExtras && (
                <>
                    {renderSubTitle(true)}
                    <Box className={choicesProductGroupStyles.productColumnRow}>
                        {productGroup.sortedExtras.map((product, index) => (
                            <ChoicesProduct
                                key={`${product.model.productId}-${productGroup.model.id}`}
                                viewModel={product}
                                productGroupViewModel={productGroup}
                                index={index}
                                isExtra={true}
                                isDisabled={isDisabled}
                            />
                        ))}
                    </Box>
                </>
            )
        );
    };

    const renderOptionsAccordionLabel = () => {
        if (viewModel.isChoicesLocked) {
            return "Options are in view only mode";
        } else {
            return "Please select your options...";
        }
    };

    const renderSubTitle = (isExtra: boolean = false) => {
        return (
            <Box
                className={choicesProductGroupStyles.subtitle}
                style={{
                    ...(portalThemeStore.primaryColour ? { backgroundColor: portalThemeStore.primaryColour } : {}),
                    ...(portalThemeStore.primaryColour ? { borderBottomColor: portalThemeStore.secondaryColour } : {}),
                }}
            >
                <Typography>{isExtra ? "...and any extras (if required)" : renderOptionsAccordionLabel()}</Typography>
            </Box>
        );
    };

    const renderNoneOfTheAbove = () => {
        const productGroup = viewModel.productGroups.find(pg => pg.model.id == productGroupId);

        return (
            productGroup && (
                <Box className={choicesProductGroupStyles.noneoftheabovecontainer}>
                    <Box>
                        <FormControlLabel
                            control={<Radio className={choicesProductGroupStyles.radioButton} />}
                            checked={productGroup.model.isNoneOfTheAbove}
                            label={<Typography variant="body2">None of the above</Typography>}
                            labelPlacement={isDesktop ? "start" : "end"}
                            name={`${productGroup.model.productGroupName}-noneoftheabove`}
                            onChange={() => !viewModel.isChoicesLocked && productGroup.setNoneOfTheAbove()}
                            className={productGroup.model.isNoneOfTheAbove ? choicesProductGroupStyles.productRadioChecked : choicesProductGroupStyles.productRadioNoneOfTheAbove}
                        />
                    </Box>
                </Box>
            )
        );
    };

    const renderSubmit = () => {
        if (!viewModel.isChoicesLocked) {
            return (
                <Box className={choicesAreaPageStyles.submitButton}>
                    <StandardButton displayName="Add to order" execute={viewModel.addToOrder} canExecute={viewModel.canAddToOrder} />
                </Box>
            );
        }
    };

    return useObserver(() => (
        <Box className={`${choicesProductGroupStyles.root}  ${pageAnimationStyles.pageWrapper} mobileDrawerHeight`}>
            <Box className={`${dashboardPageStyles.workspace}`}>
                {viewModel.ApiStatus === ApiStatus.success && (
                    <React.Fragment>
                        {isDesktop && renderBackToChoicesOrArea()}
                        {renderAreaTitleRow()}
                        {renderSubTitle()}
                        <Box style={{ paddingTop: "10px" }} className={choicesProductGroupStyles.productColumnRow}>
                            {renderChoices()}
                        </Box>
                        {renderNoneOfTheAbove()}
                        {renderExtras()}
                        {renderSubmit()}
                    </React.Fragment>
                )}
            </Box>
            {viewModel.ApiStatus === ApiStatus.loading && <React.Fragment>{renderBusy()}</React.Fragment>}
            {viewModel.ApiStatus === ApiStatus.error && <React.Fragment>{renderError()}</React.Fragment>}
        </Box>
    ));
};

export default ChoicesProductGroup;
